import { Injectable, InjectionToken, Provider } from '@angular/core';
import { ApiPaginatedResponse, PaginatedResponse } from '../../shared/pagination/paginated-response';
import { ApiSearchResult } from '../api-types/search.api';
import { SearchQuery, SearchResultItem } from '../app-types/search.app';
import { transformApiSearchResultToAppSearchResult } from '../transformers/search.transform';
import { BaseRequestService } from './base-request.service';

export const SEARCH_API = new InjectionToken<SearchService>('SEARCH_API');

@Injectable()
export class SearchService extends BaseRequestService {
	/**
	 * Searches for entities based on the provided query object. Not all fields are required to perform a search.
	 * It depends on the use case on the corresponding view.
	 * @param query The search query to use.
	 * @returns A promise that resolves to the search results.
	 */
	public search = async (query: SearchQuery) => {
		const params = this.buildSearchQueryParams(query);
		const response = await this.get<ApiPaginatedResponse<ApiSearchResult>>(`globalSearch`, params);
		const result: PaginatedResponse<SearchResultItem> = {
			entities: transformApiSearchResultToAppSearchResult(response.results),
			offset: response.offset,
			range: response.range,
			totalEntities: response.totalItems,
		};
		return result;
	};

	/**
	 * Builds the search query parameters from the provided query object.
	 * @param query The search query to use.
	 * @returns The constructed query parameters.
	 */
	private buildSearchQueryParams = (query: SearchQuery): Record<string, unknown> => {
		const params: Record<string, unknown> = {};
		if (query.clientId) params['ClientId'] = query.clientId;
		if (query.offset) params['Offset'] = query.offset;
		if (query.range) params['Range'] = query.range;
		params['SearchClients'] = query.searchClients;
		params['SearchUsers'] = query.searchUsers;
		params['SearchClientEnvironments'] = query.searchClientEnvironments;
		params['SearchInstitutions'] = query.searchInstitutions;
		params['SearchInstitutionPerson'] = query.searchInstitutionPerson;
		params['SearchSponsors'] = query.searchSponsors;
		params['SearchInstitutionBranches'] = query.searchInstitutionBranches;
		params['SearchISO'] = query.searchIso;
		if (query.searchSponsorPerson !== undefined) {
			params['SearchSponsorPerson'] = query.searchSponsorPerson;
		}
		if (query.searchAssignments !== undefined) {
			params['SearchAssignments'] = query.searchAssignments;
		}
		if (query.searchTerm) {
			params['SearchTerm'] = query.searchTerm;
		}
		if (query.includeContactPoints !== undefined) {
			params['IncludeContactPoints'] = query.includeContactPoints;
		}
		return params;
	};
}

export const provideSearchApi: Provider = {
	provide: SEARCH_API,
	useClass: SearchService,
};
